import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import LZString from 'lz-string';
import { generateAvatarSvg } from '@/lib/avatars';

// Types
export interface Person {
  id: string;
  name: string;
  avatar: string;
  timezone: string;
  location: string;
  workHourStart: number;
  workHourEnd: number;
  source: 'manual' | 'shared';  // manual = added by user, shared = real person
  // Live activity status (only for shared/real users)
  activityStatus?: 'online' | 'away' | 'offline' | null;
  lastSeen?: number;
}

export interface MyProfile {
  id: string;
  name: string;
  avatar: string;
  timezone: string;
  location: string;
  workHourStart: number;
  workHourEnd: number;
  showActivityStatus?: boolean;  // Privacy: allow others to see your activity
}

export interface Settings {
  theme: 'light' | 'dark' | 'auto';
  use24Hour: boolean;
  showSeconds: boolean;
  showTips: boolean;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: {
    uid: string;
    email: string;
    displayName: string;
    photoURL: string;
  } | null;
}

interface StoreState {
  // Profile
  me: MyProfile;
  isProfileSetup: boolean;
  
  // People
  people: Person[];
  
  // Settings
  settings: Settings;
  
  // Auth
  auth: AuthState;
  
  // Actions - Profile
  setMe: (profile: Partial<MyProfile>) => void;
  completeProfileSetup: () => void;
  
  // Actions - People
  addPerson: (person: Omit<Person, 'id' | 'source'>) => void;
  updatePerson: (id: string, data: Partial<Person>) => void;
  removePerson: (id: string) => void;
  updatePeopleActivityStatus: (statuses: Record<string, { status: string; lastSeen: number } | null>) => void;
  
  // Actions - Settings
  updateSettings: (settings: Partial<Settings>) => void;
  
  // Actions - Auth
  setAuth: (auth: AuthState) => void;
  signOut: () => void;
  
  // Actions - Data
  initFromLocalStorage: () => void;
  initFromUrl: (encoded: string) => void;
  exportData: () => string;
  importData: (data: string) => void;
  getShareUrl: () => string;
}

// Generate UUID
const generateId = () => crypto.randomUUID();

// Get user's timezone
const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

const defaultMe: MyProfile = {
  id: generateId(),
  name: 'You',  // Default name so timeline shows immediately
  avatar: generateAvatarSvg(0),
  timezone: getUserTimezone(),
  location: '',
  workHourStart: 9,
  workHourEnd: 18,
  showActivityStatus: true,  // Default: share activity status
};

const defaultSettings: Settings = {
  theme: 'auto',
  use24Hour: true,
  showSeconds: true,
  showTips: true,
};

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      // Initial state
      me: defaultMe,
      isProfileSetup: true,  // Auto-complete so timeline shows immediately
      people: [],
      settings: defaultSettings,
      auth: {
        isAuthenticated: false,
        user: null,
      },
      
      // Profile actions
      setMe: (profile) => set((state) => ({
        me: { ...state.me, ...profile },
      })),
      
      completeProfileSetup: () => set({ isProfileSetup: true }),
      
      // People actions
      addPerson: (person) => set((state) => ({
        people: [
          ...state.people,
          {
            ...person,
            id: generateId(),
            source: 'manual' as const,
          },
        ],
      })),
      
      updatePerson: (id, data) => set((state) => ({
        people: state.people.map((p) =>
          p.id === id ? { ...p, ...data } : p
        ),
      })),
      
      removePerson: (id) => set((state) => ({
        people: state.people.filter((p) => p.id !== id),
      })),
      
      updatePeopleActivityStatus: (statuses) => set((state) => ({
        people: state.people.map((p) => {
          const status = statuses[p.id];
          if (status && p.source === 'shared') {
            return {
              ...p,
              activityStatus: status.status as 'online' | 'away' | 'offline',
              lastSeen: status.lastSeen,
            };
          }
          return p;
        }),
      })),
      
      // Settings actions
      updateSettings: (newSettings) => set((state) => ({
        settings: { ...state.settings, ...newSettings },
      })),
      
      // Auth actions
      setAuth: (auth) => set({ auth }),
      
      signOut: () => set({
        auth: { isAuthenticated: false, user: null },
      }),
      
      // Data actions
      initFromLocalStorage: () => {
        // Zustand persist handles this automatically
      },
      
      initFromUrl: (encoded) => {
        try {
          const decoded = LZString.decompressFromEncodedURIComponent(encoded);
          if (!decoded) return;
          
          const data = JSON.parse(decoded);
          
          // Import the data
          if (data.m) {
            set({
              me: {
                id: generateId(),
                name: data.m.n || '',
                avatar: data.m.a || '😀',
                timezone: data.m.t || getUserTimezone(),
                location: data.m.l || '',
                workHourStart: data.m.w?.[0] ?? 9,
                workHourEnd: data.m.w?.[1] ?? 18,
              },
              isProfileSetup: true,
            });
          }
          
          if (data.p && Array.isArray(data.p)) {
            set({
              people: data.p.map((p: any) => ({
                id: generateId(),
                name: p.n || '',
                avatar: p.a || '👤',
                timezone: p.t || 'UTC',
                location: p.l || '',
                workHourStart: p.w?.[0] ?? 9,
                workHourEnd: p.w?.[1] ?? 17,
                source: 'manual' as const,
              })),
            });
          }
          
          // Clear URL params after import
          window.history.replaceState({}, '', window.location.pathname);
        } catch (e) {
          console.error('Failed to parse shared data:', e);
        }
      },
      
      exportData: () => {
        const { me, people } = get();
        const data = {
          m: {
            n: me.name,
            a: me.avatar,
            t: me.timezone,
            l: me.location,
            w: [me.workHourStart, me.workHourEnd],
          },
          p: people.map((p) => ({
            n: p.name,
            a: p.avatar,
            t: p.timezone,
            l: p.location,
            w: [p.workHourStart, p.workHourEnd],
          })),
        };
        return JSON.stringify(data, null, 2);
      },
      
      importData: (jsonString) => {
        try {
          const data = JSON.parse(jsonString);
          if (data.m) {
            set({
              me: {
                id: get().me.id,
                name: data.m.n || '',
                avatar: data.m.a || '😀',
                timezone: data.m.t || getUserTimezone(),
                location: data.m.l || '',
                workHourStart: data.m.w?.[0] ?? 9,
                workHourEnd: data.m.w?.[1] ?? 18,
              },
            });
          }
          if (data.p && Array.isArray(data.p)) {
            set({
              people: data.p.map((p: any) => ({
                id: generateId(),
                name: p.n || '',
                avatar: p.a || '👤',
                timezone: p.t || 'UTC',
                location: p.l || '',
                workHourStart: p.w?.[0] ?? 9,
                workHourEnd: p.w?.[1] ?? 17,
                source: 'manual' as const,
              })),
            });
          }
        } catch (e) {
          console.error('Failed to import data:', e);
        }
      },
      
      getShareUrl: () => {
        const { me, people } = get();
        const data = {
          m: {
            n: me.name,
            a: me.avatar,
            t: me.timezone,
            l: me.location,
            w: [me.workHourStart, me.workHourEnd],
          },
          p: people.map((p) => ({
            n: p.name,
            a: p.avatar,
            t: p.timezone,
            l: p.location,
            w: [p.workHourStart, p.workHourEnd],
          })),
        };
        const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(data));
        return `${window.location.origin}/?d=${compressed}`;
      },
    }),
    {
      name: 'clocksync-storage',
      partialize: (state) => ({
        me: state.me,
        isProfileSetup: state.isProfileSetup,
        people: state.people,
        settings: state.settings,
        // Don't persist auth - Firebase handles that
      }),
    }
  )
);
